/* src/core/store.ts — the typed observable the whole client hangs off (PLAN.md Part 2 §6,
 * TASKS.md 0.6).
 *
 * No framework, so no framework state. `gameStore` holds the last `GET /api/game/{playerId}`
 * response verbatim; net/ writes it, ui/ subscribes to it, and nothing else is allowed to be
 * a source of truth for game data.
 */

import type { GameState } from './types.ts';

export interface Store<T> {
  get(): T;
  set(next: T): void;
  subscribe(fn: (s: T) => void): () => void;
  /**
   * Subscribe to a derived slice. `onChange` fires only when the selected value actually
   * changes, so a component bound to `cash` is not woken by an unrelated `market` update.
   */
  select<U>(selector: (s: T) => U, onChange: (u: U) => void): () => void;
}

/**
 * Reference equality, then one level of own-key comparison. Deliberately shallow: poll
 * responses arrive as freshly parsed objects, so a deep compare would walk the entire
 * 39-key game blob several times a second to discover what a selector already knows.
 */
export function shallowEqual(a: unknown, b: unknown): boolean {
  if (Object.is(a, b)) return true;
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;

  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;

  const bRecord = b as Record<string, unknown>;
  const aRecord = a as Record<string, unknown>;
  for (const key of aKeys) {
    if (!Object.hasOwn(bRecord, key)) return false;
    if (!Object.is(aRecord[key], bRecord[key])) return false;
  }
  return true;
}

export function createStore<T>(initial: T): Store<T> {
  let state = initial;
  const subs = new Set<(s: T) => void>();

  const api: Store<T> = {
    get: () => state,

    set(next: T): void {
      if (shallowEqual(state, next)) return;
      state = next;
      // Copy before iterating: a subscriber may unsubscribe itself from its callback.
      for (const fn of [...subs]) fn(state);
    },

    subscribe(fn: (s: T) => void): () => void {
      subs.add(fn);
      return () => {
        subs.delete(fn);
      };
    },

    select<U>(selector: (s: T) => U, onChange: (u: U) => void): () => void {
      let last = selector(state);
      return api.subscribe((s) => {
        const next = selector(s);
        if (shallowEqual(last, next)) return;
        last = next;
        onChange(next);
      });
    },
  };

  return api;
}

/** The one game-state store. `null` until the first poll resolves — selectors must handle it. */
export const gameStore: Store<GameState | null> = createStore<GameState | null>(null);

/**
 * Client-only view state. Deliberately separate from `gameStore`: nothing here is
 * server-authoritative, and mixing the two would mean a poll response could clobber which
 * overlay the player has open.
 */
export type ViewMode = 'WORLD' | 'FOOT TRAFFIC' | 'LOGISTICS' | 'RESOURCES';

export interface UiState {
  readonly viewMode: ViewMode;
  readonly overlays: Readonly<Record<string, boolean>>;
}

export const uiStore: Store<UiState> = createStore<UiState>({
  viewMode: 'WORLD',
  overlays: { labels: true, ownership: false, grid: false },
});
